import React from 'react';
import { cn } from "@/lib/utils";
import { STORY_TEMPLATES } from '@/lib/storyTemplates';
import { Card, CardContent } from '@/components/ui/card';
import { Check } from 'lucide-react';

interface TimelineTemplateStepProps {
  selectedTemplate: string;
  setSelectedTemplate: (templateId: string) => void;
  storyId: string | null;
  timelineName: string;
  setTimelineName: (name: string) => void;
}

export function TimelineTemplateStep({
  selectedTemplate,
  setSelectedTemplate,
  timelineName,
  setTimelineName
}: TimelineTemplateStepProps) {

  const templates = [
    ...Object.entries(STORY_TEMPLATES).map(([id, template]) => ({
      id,
      name: template.name,
      chapters: template.recommendedChapters,
      type: template.recommendedChapters > 10 ? 'Chapters' : 'Beats'
    })),
    { id: 'empty', name: 'Not sure yet', chapters: 0, type: 'Blank canvas' }
  ];

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-700">
      {/* Header section matching Step 0 exactly */}
      <div className="space-y-3">
        <h1 className="text-3xl font-bold tracking-tight text-foreground font-lora">
          Pick a story structure
        </h1>
        <p className="text-muted-foreground text-sm leading-relaxed max-w-md">
          Generates your chapter plan and beats. You can change it anytime from Timeline.
        </p>
      </div>

      {/* compact Grid of structure cards */}
      <div className="space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-xl mx-auto overflow-y-visible">
          {templates.map((template) => {
            const isSelected = selectedTemplate === template.id;
            return (
              <Card
                key={template.id}
                onClick={() => {
                  setSelectedTemplate(template.id);
                  if (template.id !== 'empty') {
                    setTimelineName(`${template.name} Timeline`);
                  } else {
                    setTimelineName('Main Timeline');
                  }
                }}
                className={cn(
                  "group relative cursor-pointer transition-all duration-200 border-border/40 hover:border-primary/40 overflow-hidden rounded-xl h-16",
                  isSelected
                    ? "bg-primary/5 ring-1 ring-primary border-primary shadow-sm shadow-primary/5"
                    : "bg-muted/5 hover:bg-muted/10"
                )}
              >
                <CardContent className="px-4 py-0 h-full flex items-center justify-between">
                  <div className="space-y-0.5">
                    <h3 className={cn(
                      "font-bold text-xs tracking-tight transition-colors line-clamp-1 leading-tight",
                      isSelected ? "text-primary" : "text-foreground group-hover:text-primary/80"
                    )}>
                      {template.name}
                    </h3>
                    <p className="text-[9px] font-bold text-muted-foreground/40 uppercase tracking-widest flex items-center gap-1.5 font-sans leading-none">
                      {template.id === 'empty' ? (
                        <span>Start from scratch</span>
                      ) : (
                        <>
                          <span className={cn(isSelected ? "text-primary" : "text-muted-foreground/60")}>{template.chapters}</span>
                          <span>{template.type}</span>
                        </>
                      )}
                    </p>
                  </div>

                  {isSelected && (
                    <div className="h-4 w-4 rounded-full bg-primary flex items-center justify-center animate-in zoom-in duration-300 ml-2 shadow-sm shrink-0">
                      <Check className="h-2.5 w-2.5 text-primary-foreground stroke-[3px]" />
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
